import { ChatCitation, useCitedText } from "./ChatCitations";
import { cn } from "@/lib/utils/cn";
import type { ComponentPropsWithoutRef } from "react";
import { memo, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { sandboxArtifactDownloadOptions } from "@/lib/data/react-query/sandbox-artifacts";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import { ChatArtifactVisual } from "./ChatVisual";
import { ARTIFACT_REFERENCE } from "./chat-visual-document";
import remarkGfm from "remark-gfm";
import { PreserveMarkdownSelection } from "@/components/ui/AiChatPanel/PreserveMarkdownSelection";
import { createIncrementalMarkdownPlugin } from "@/components/ui/AiChatPanel/incremental-markdown";

const markdownClassName =
  "min-w-0 break-words text-[15px] leading-7 [overflow-wrap:anywhere] [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_a]:text-[#3B5BF5] [&_a]:underline [&_a]:underline-offset-2 [&_blockquote]:my-4 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:text-muted-foreground [&_code]:rounded [&_code]:bg-muted/70 [&_code]:px-1.5 [&_code]:py-0.5 [&_h1]:mb-3 [&_h1]:mt-6 [&_h1]:text-xl [&_h1]:font-semibold [&_h2]:mb-2 [&_h2]:mt-5 [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:mb-2 [&_h3]:mt-4 [&_h3]:font-semibold [&_li]:my-1 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-3 [&_pre]:my-4 [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:rounded-xl [&_pre]:border [&_pre]:border-border/70 [&_pre]:bg-muted/60 [&_pre]:p-4 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-2 [&_th]:border [&_th]:border-border [&_th]:bg-muted/50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6";

type ChatMarkdownProps = {
  children: string;
  className?: string;
};

function ExternalLink({
  href,
  children,
  ...props
}: ComponentPropsWithoutRef<"a">) {
  const citation = href?.match(/^#lawworks-source-(\d+)$/);
  if (citation) return <ChatCitation number={Number(citation[1])} />;
  if (href === "#lawworks-source-unavailable") return <ChatCitation />;
  const artifact = href?.match(ARTIFACT_REFERENCE);
  if (artifact) {
    return (
      <ArtifactDownloadLink id={artifact[1]}>{children}</ArtifactDownloadLink>
    );
  }
  const isExternal =
    href?.startsWith("http://") || href?.startsWith("https://");

  return (
    <a
      {...props}
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noreferrer" : undefined}
    >
      {children}
    </a>
  );
}

function ArtifactDownloadLink({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  const download = useMutation(sandboxArtifactDownloadOptions(id));
  const downloadFile = async () => {
    if (download.isPending) return;
    try {
      const result = await download.mutateAsync("attachment");
      const anchor = document.createElement("a");
      anchor.href = result.url;
      anchor.download = "";
      anchor.rel = "noopener noreferrer";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch {
      // Keep the link available to retry and show the request failure below.
    }
  };
  return (
    <span>
      <button
        type="button"
        className="cursor-pointer text-left text-[#3B5BF5] underline underline-offset-2"
        disabled={download.isPending}
        aria-busy={download.isPending}
        onClick={() => void downloadFile()}
      >
        {children}
      </button>
      {download.isError && (
        <span role="alert" className="text-destructive ml-2 text-sm">
          This file could not be downloaded. Try again.
        </span>
      )}
    </span>
  );
}

// Stable component types keep embedded previews mounted as the timeline updates.
function MarkdownImage({ src, alt }: ComponentPropsWithoutRef<"img">) {
  const match = src?.match(ARTIFACT_REFERENCE);
  return match ? (
    <ChatArtifactVisual key={match[1]} id={match[1]} title={alt ?? "Visual"} />
  ) : (
    <img src={src} alt={alt ?? ""} loading="lazy" />
  );
}

function MarkdownTable({
  children,
  ...props
}: ComponentPropsWithoutRef<"table">) {
  return (
    <div className="border-border/70 my-4 max-w-full overflow-x-auto rounded-xl border">
      <table {...props}>{children}</table>
    </div>
  );
}

const markdownComponents = {
  a: ExternalLink,
  img: MarkdownImage,
  table: MarkdownTable,
};

type MarkdownNode = {
  type: string;
  value?: string;
  url?: string;
  children?: MarkdownNode[];
};

// Resolve provider references through the message's trusted citation metadata.
// Markdown code and existing links remain literal.
function remarkFileCitations() {
  const marker = "\uE200filecite";
  return function transform(node: MarkdownNode) {
    if (!node.children || node.type === "link" || node.type === "linkReference")
      return;
    node.children = node.children.flatMap((child): MarkdownNode[] => {
      if (child.type !== "text" || !child.value) {
        transform(child);
        return [child];
      }
      const pieces: MarkdownNode[] = [];
      let cursor = 0;
      for (const match of child.value.matchAll(
        /\uE200filecite\uE202([^\uE201\n]*)\uE201/g,
      )) {
        pieces.push({
          type: "text",
          value: child.value.slice(cursor, match.index),
        });
        pieces.push({
          type: "link",
          url: "#lawworks-source-unavailable",
          children: [{ type: "text", value: "Source unavailable" }],
        });
        cursor = match.index + match[0].length;
      }
      let tail = child.value.slice(cursor);
      const start = tail.lastIndexOf("\uE200");
      if (start !== -1) {
        const pending = tail.slice(start);
        if (
          marker.startsWith(pending) ||
          (pending.startsWith(`${marker}\uE202`) && !/[\n\uE201]/.test(pending))
        ) {
          tail = tail.slice(0, start);
        }
      }
      pieces.push({ type: "text", value: tail });
      return pieces;
    });
  };
}

export const ChatMarkdown = memo(function ChatMarkdown({
  children,
  className,
}: ChatMarkdownProps) {
  const citedText = useCitedText(children);
  const remarkPlugins = useMemo(
    () => [remarkGfm, remarkFileCitations, createIncrementalMarkdownPlugin()],
    [],
  );
  return (
    <PreserveMarkdownSelection>
      {(root) => (
        <div ref={root} className={cn(markdownClassName, className)}>
          <ReactMarkdown
            remarkPlugins={remarkPlugins}
            urlTransform={(url, key, node) =>
              ((key === "src" && node.tagName === "img") ||
                (key === "href" && node.tagName === "a")) &&
              ARTIFACT_REFERENCE.test(url)
                ? url
                : defaultUrlTransform(url)
            }
            components={markdownComponents}
          >
            {citedText}
          </ReactMarkdown>
        </div>
      )}
    </PreserveMarkdownSelection>
  );
});
